import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import translations from '@/i18n/translations';

const LanguageContext = createContext();

const SUPPORTED_LANGS = ['es', 'en', 'fr', 'de', 'nl', 'hi', 'zh'];

const detectLangFromURL = () => {
  const pathLang = window.location.pathname.split('/')[1];
  if (SUPPORTED_LANGS.includes(pathLang)) return pathLang;
  return null;
};

const detectBrowserLang = () => {
  const browserLang = navigator.language.split('-')[0];
  return SUPPORTED_LANGS.includes(browserLang) ? browserLang : 'es';
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Priority: URL path > localStorage > browser > 'es'
    const urlLang = detectLangFromURL();
    if (urlLang) return urlLang;
    const saved = localStorage.getItem('vf_language');
    if (saved && SUPPORTED_LANGS.includes(saved)) return saved;
    return detectBrowserLang();
  });

  const setLanguage = useCallback((newLang) => {
    if (!SUPPORTED_LANGS.includes(newLang)) return;
    setLanguageState(newLang);
    localStorage.setItem('vf_language', newLang);

    // Update URL if we're on a language-prefixed public page
    const path = window.location.pathname;
    const pathParts = path.split('/');
    if (SUPPORTED_LANGS.includes(pathParts[1])) {
      pathParts[1] = newLang;
      const newPath = pathParts.join('/');
      window.history.replaceState(null, '', newPath);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('vf_language', language);
    document.documentElement.lang = language;
  }, [language]);

  // Sync language from URL on popstate (back/forward navigation)
  useEffect(() => {
    const handlePop = () => {
      const urlLang = detectLangFromURL();
      if (urlLang && urlLang !== language) {
        setLanguageState(urlLang);
        localStorage.setItem('vf_language', urlLang);
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations['en']?.[key] || translations['es']?.[key] || key;
  };

  const availableLanguages = [
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'nl', name: 'Nederlands', flag: '🇧🇪' },
    { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
  ];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, availableLanguages }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export { SUPPORTED_LANGS };
export default LanguageContext;
